import { AdSlides } from "src/store/ads-analitics.store";
import { FunnelAdsData } from "./lib";
import { tooltipFormatter } from "./tooltip";

export const seriesConfig = [
  { name: "Amazon Ads", color: "#f39c6b" },
  { name: "Amazon Organic", color: "#81c784" },
  { name: "Organic Social", color: "#8e44ad" },
];

const seriesColors: Record<string, string> = {
  "Amazon Ads": "#f39c6b",
  "Forecast Amazon Ads": "#FEB38A",
  "Amazon Organic": "#81c784",
  "Forecast Amazon Organic": "#98E191",
  "Organic Social": "#8e44ad",
  DSP: "#3498db",
};

const dates = [
  new Date(2024, 0, 1), // Impressions
  new Date(2024, 0, 2), // Clicks
  new Date(2024, 0, 3),
];

const stageNames = ["Impressions", "Clicks", "Units Sold"] as const;
const stageScale: Record<(typeof stageNames)[number], number> = {
  Impressions: 1,
  Clicks: 50,
  "Units Sold": 100,
};

function prepareForecastData(
  baseData: FunnelAdsData,
  whatIfData: FunnelAdsData | undefined,
  adSlides: AdSlides,
  activeNames: string[],
): Record<string, number[]> {
  if (whatIfData) {
    return Object.fromEntries(
      activeNames.map((name) => [name, [...(whatIfData[name] ?? [])]]),
    );
  }
  const sum = Object.values(adSlides).reduce((a, v) => a + v, 0) || 1;
  const factor = sum / 2;
  return Object.fromEntries(
    activeNames.map((name) => [
      `Forecast ${name}`,
      baseData[name].map(() => factor),
    ]),
  );
}

function calculateStageValues(
  baseData: FunnelAdsData,
  idx: number,
  activeNames: string[],
  stage: (typeof stageNames)[number],
): Record<string, number> {
  return Object.fromEntries(
    activeNames.map((name) => {
      let v = baseData[name][idx] ?? 0;
      if (name.startsWith("Amazon") && stage !== "Impressions")
        v *= stageScale[stage];
      return [name, v];
    }),
  );
}

interface SeriesPointOptions {
  date: Date;
  idx: number;
  name: string;
  stage: (typeof stageNames)[number];
  stageVals: Record<string, number>;
  maxSeries: string;
  forecastData: Record<string, number[]>;
  hasForecast: boolean;
  useWhatIf: boolean;
  originalWhatIf?: FunnelAdsData;
  maxImpr: number;
}

function buildSeriesPoints({
  date,
  idx,
  name,
  stage,
  stageVals,
  maxSeries,
  forecastData,
  hasForecast,
  useWhatIf,
  originalWhatIf,
  maxImpr,
}: SeriesPointOptions): any[] {
  let val = stageVals[name];
  if (stage === "Units Sold" || name === maxSeries) val *= 2;
  const pct = maxImpr ? (val / maxImpr) * 100 : 0;

  const points: any[] = [[date.getTime(), pct, name]];
  if (!hasForecast) return points;

  const key = useWhatIf ? name : `Forecast ${name}`;
  const delta = forecastData[key]?.[idx] || 0;
  let fVal = delta;
  if (stage === "Clicks") fVal *= stageScale["Clicks"] * 1.5;
  if (stage === "Units Sold") fVal *= stageScale["Units Sold"] * 3;
  const pctF = maxImpr ? (fVal / maxImpr) * 100 : 0;
  const fPoint: any = [date.getTime(), pctF, `Forecast ${name}`];
  if (useWhatIf && originalWhatIf)
    fPoint.originalWhatIfValue = originalWhatIf[name][idx];
  points.push(fPoint);
  return points;
}

function buildThemeRiverData(
  baseData: FunnelAdsData,
  forecastData: Record<string, number[]>,
  activeNames: string[],
  hasForecast: boolean,
  useWhatIf: boolean,
  originalWhatIf: FunnelAdsData | undefined,
  maxImpr: number,
): any[] {
  return dates.flatMap((date, idx) => {
    const stage = stageNames[idx];
    const stageVals = calculateStageValues(baseData, idx, activeNames, stage);
    const maxSeries = Object.entries(stageVals).reduce(
      (b, c) => (c[1] > b[1] ? c : b),
      Object.entries(stageVals)[0],
    )[0];

    return activeNames.flatMap((name) =>
      buildSeriesPoints({
        date,
        idx,
        name,
        stage,
        stageVals,
        maxSeries,
        forecastData,
        hasForecast,
        useWhatIf,
        originalWhatIf,
        maxImpr,
      }),
    );
  });
}

function createFormatter(
  dateRange: { startDate: Date; endDate: Date },
  allMetrics: Record<string, number>,
  isWhatIf: boolean,
) {
  return function formatter(params: any) {
    const id = `echarts-tooltip-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    const holder = `<div id="${id}">Loading...</div>`;

    if (
      isWhatIf &&
      params.data?.originalWhatIfValue !== undefined &&
      params.seriesName.startsWith("Forecast ")
    ) {
      params.value[1] = params.data.originalWhatIfValue;
    }

    tooltipFormatter(params, dateRange, allMetrics, isWhatIf)
      .then((html) => {
        const el = document.getElementById(id);
        if (el) {
          el.innerHTML = html;
        }
      })
      .catch(() => {
        const el = document.getElementById(id);
        if (el) {
          el.innerHTML = "<div>Error loading data</div>";
        }
      });

    return holder;
  };
}

export function getOption(
  activeSeries: Record<string, boolean>,
  adSlides: AdSlides,
  baseData: FunnelAdsData,
  whatIfData: FunnelAdsData | undefined,
  dateRange: { startDate: Date; endDate: Date },
  isWhatIf = false,
  allMetrics: Record<string, number> = {},
): echarts.EChartsOption {
  const activeNames = seriesConfig
    .filter((s) => activeSeries[s.name])
    .map((s) => s.name);
  const hasWhatIf = isWhatIf && !!whatIfData;
  const needsForecast =
    hasWhatIf || Object.values(adSlides).some((v) => v !== 0);
  const dataClone = { ...baseData };
  const originalWhatIf = whatIfData ? { ...whatIfData } : undefined;
  const forecastData = needsForecast
    ? prepareForecastData(baseData, whatIfData, adSlides, activeNames)
    : {};

  const maxImpr = Math.max(
    allMetrics.ad_impressions || baseData["Amazon Ads"][0],
    allMetrics.organicImpressions || baseData["Amazon Organic"][0],
  );

  const filteredData = buildThemeRiverData(
    dataClone,
    forecastData,
    activeNames,
    needsForecast,
    hasWhatIf,
    originalWhatIf,
    maxImpr,
  );

  const colors = Object.keys(seriesColors).reduce<string[]>(
    (acc, key) =>
      filteredData.some((pt: any) => pt[2] === key)
        ? [...acc, seriesColors[key]]
        : acc,
    [],
  );

  return {
    title: {
      text: "Campaign Performance",
      left: "0%",
      top: "24px",
      textStyle: { color: "#ffffff" },
    },
    tooltip: {
      trigger: "item",
      axisPointer: { type: "shadow" },
      backgroundColor: "#ffffff",
      borderRadius: 16,
      renderMode: "html",
      padding: 16,
      position: (point, _, __, ___, size) => {
        const chartWidth = 1252;
        const center = chartWidth / 2;
        const threshold = chartWidth * 0.18;
        if (point[0] >= center - threshold && point[0] <= center + threshold) {
          return [point[0] - size.contentSize[0] / 2, point[1] + 15];
        }
        return point[0] > center
          ? [point[0] - size.contentSize[0] - 15, point[1]]
          : [point[0] + 15, point[1]];
      },
      formatter: createFormatter(dateRange, allMetrics, isWhatIf),
    },
    singleAxis: {
      type: "time",
      top: "5%",
      left: "0%",
      right: "0%",
      bottom: "5%",
      show: false,
      axisLabel: {
        show: false,
      },
      axisTick: {
        show: false,
      },
      axisLine: {
        show: false,
      },
      splitLine: {
        show: false,
      },
    },
    graphic: [
      {
        type: "text",
        left: "15%",
        top: "95%",
        style: {
          text: "Impressions",
          align: "center",
          fill: "#ffffff",
        },
      },
      {
        type: "text",
        left: "50%",
        top: "95%",
        style: {
          text: "Clicks",
          align: "center",
          fill: "#ffffff",
        },
      },
      {
        type: "text",
        left: "83%",
        top: "95%",
        style: {
          text: "Units Sold",
          align: "center",
          fill: "#ffffff",
        },
      },
    ],
    series: [
      {
        type: "themeRiver",
        data: filteredData,
        animation: false,
        label: {
          show: false,
          color: "#ffffff",
          margin: 0,
        },
        color: colors,
        emphasis: {
          disabled: false,
          focus: "self",
          itemStyle: {
            opacity: 1,
          },
        },
        select: {
          disabled: true,
        },
        blur: {
          itemStyle: {
            opacity: 0.2,
          },
        },
        universalTransition: true,
      },
    ],
  };
}
